import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ProjectCreationAnalytics } from '../../services/projectCreationAnalytics';
import { 
  ProjectCreationSettingsService,
  ProjectCreationEligibility
} from '../../services/projectCreationSettingsService';
import ProjectCreationRequirements from './ProjectCreationRequirements';
import ErrorDisplay from '../ui/ErrorDisplay';
import { useErrorHandling } from '../../hooks/useErrorHandling';
import { AppError } from '../../types/error';

interface ProjectCreationGuardProps {
  children: React.ReactNode;
  onAccessDenied?: (eligibility: ProjectCreationEligibility) => void;
  fallbackComponent?: React.ComponentType<{ eligibility: ProjectCreationEligibility; error?: AppError }>;
}

interface GuardState {
  eligibility: ProjectCreationEligibility | null;
  loading: boolean;
  error: AppError | null;
  retryCount: number;
}

const ProjectCreationGuard: React.FC<ProjectCreationGuardProps> = ({ 
  children, 
  onAccessDenied,
  fallbackComponent: FallbackComponent
}) => {
  const { user } = useAuth();
  const { executeWithErrorHandling, error, clearError } = useErrorHandling();
  const [state, setState] = useState<GuardState>({
    eligibility: null,
    loading: true,
    error: null,
    retryCount: 0
  });

  // Check eligibility with error handling
  const checkEligibility = useCallback(async (): Promise<void> => {
    if (!user?.id) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: null,
        eligibility: null
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    const result = await executeWithErrorHandling(
      async () => {
        const eligibility = await ProjectCreationSettingsService.checkEligibility(user.id);
        
        // Log the attempt for analytics
        await ProjectCreationAnalytics.logAttempt(
          user.id,
          eligibility.canCreate,
          eligibility.canCreate ? undefined : eligibility.reasons
        );
        
        return eligibility;
      },
      'eligibility_check'
    );

    if (result) {
      setState(prev => ({
        ...prev,
        eligibility: result,
        loading: false,
        error: null,
        retryCount: 0
      }));

      // Call onAccessDenied callback if access is denied
      if (!result.canCreate && onAccessDenied) {
        onAccessDenied(result);
      }
    } else {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error,
        retryCount: prev.retryCount + 1
      }));
    }
  }, [user?.id, onAccessDenied, executeWithErrorHandling, error]);

  // Initial eligibility check
  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  // Real-time updates removed to prevent unnecessary API polling
  // The eligibility is checked once on mount and can be manually refreshed
  // Future enhancement: implement WebSocket or Supabase real-time subscriptions

  // Manual retry function
  const handleRetry = useCallback(() => {
    checkEligibility();
  }, [checkEligibility]);

  // Loading state
  if (state.loading) {
    return (
      <div className="project-creation-guard-loading">
        <div className="loading-state">
          <div className="spinner-border text-primary" role="status">
            <p className="visually-hidden">Checking eligibility...</p>
          </div>
        </div>
        <p className="mt-3 text-muted">Checking project creation eligibility...</p>
      </div>
    );
  }

  // Error state with retry option
  if (state.error || error) {
    const displayError = state.error || error;
    
    if (FallbackComponent && displayError) {
      return <FallbackComponent eligibility={state.eligibility!} error={displayError} />;
    }

    return (
      <div className="project-creation-guard-error">
        <ErrorDisplay
          error={displayError}
          onRetry={handleRetry}
        />
      </div>
    );
  }

  // Access denied state
  if (state.eligibility && !state.eligibility.canCreate) {
    if (FallbackComponent) {
      return <FallbackComponent eligibility={state.eligibility} />;
    }

    return (
      <ProjectCreationRequirements 
        eligibility={state.eligibility}
        onRetry={handleRetry}
      />
    );
  }

  // Access granted - render children
  return <>{children}</>;
};

export default ProjectCreationGuard;